import type { ApiEndpointDefinition } from '../api-endpoint.types';
import type { EventActionResponse } from '../../models/entities/event-action.model';
import type { EventAlarmsResponse } from '../../models/entities/event-detail.model';
import type { CameraPtzResponse } from '../../models/entities/camera-ptz.model';
import type { DisarmCameraResponse } from '../../models/entities/camera-arm-disarm.model';
import type { StartPatrolSessionResponse } from '../../models/entities/patrol-session.model';
import type { EventsQueueResponse } from '../../models/entities/events-queue.model';

/**
 * Dealer OpenAPI v1 paths (prefixed `/api/dealer` for EBM / YARP).
 * Registered for contract parity even when Agent UI still calls mock-oriented aliases.
 */
export const DEALER_OPENAPI_ENDPOINTS = {
  /** Real GET /events (queue source). Alias: eventsQueue → …/events/queue */
  dealerEvents: {
    id: 'dealer-events',
    method: 'GET',
    apiPath: '/api/dealer/events',
    page: 'events',
    entity: 'dealer-events',
  } satisfies ApiEndpointDefinition<EventsQueueResponse>,
  dealerEventsClaim: {
    id: 'dealer-events-claim',
    method: 'POST',
    apiPath: '/api/dealer/events/claim',
    page: 'events',
    entity: 'dealer-events-claim',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerEventsInProgress: {
    id: 'dealer-events-in-progress',
    method: 'GET',
    apiPath: '/api/dealer/events/in-progress',
    page: 'events',
    entity: 'dealer-events-in-progress',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerEventsParked: {
    id: 'dealer-events-parked',
    method: 'GET',
    apiPath: '/api/dealer/events/parked',
    page: 'events',
    entity: 'dealer-events-parked',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerEventById: {
    id: 'dealer-event-by-id',
    method: 'GET',
    apiPath: '/api/dealer/events/:eventId',
    page: 'events',
    entity: 'dealer-event-by-id',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerEventAlarms: {
    id: 'dealer-event-alarms',
    method: 'GET',
    apiPath: '/api/dealer/events/:eventId/alarms',
    page: 'events',
    entity: 'dealer-event-alarms',
  } satisfies ApiEndpointDefinition<EventAlarmsResponse>,
  dealerEventAlarmsFlag: {
    id: 'dealer-event-alarms-flag',
    method: 'POST',
    apiPath: '/api/dealer/events/:eventId/alarms/flag',
    page: 'events',
    entity: 'dealer-event-alarms-flag',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerEventAlarmsFlagged: {
    id: 'dealer-event-alarms-flagged',
    method: 'GET',
    apiPath: '/api/dealer/events/:eventId/alarms/flagged',
    page: 'events',
    entity: 'dealer-event-alarms-flagged',
  } satisfies ApiEndpointDefinition<EventAlarmsResponse>,
  /** Scalar CloseEvent — PATCH /events/{eventId}/close */
  dealerEventClosePatch: {
    id: 'dealer-event-close-patch',
    method: 'PATCH',
    apiPath: '/api/dealer/events/:eventId/close',
    page: 'events',
    entity: 'dealer-event-close-patch',
  } satisfies ApiEndpointDefinition<EventActionResponse>,
  dealerEventPark: {
    id: 'dealer-event-park',
    method: 'POST',
    apiPath: '/api/dealer/events/:eventId/park',
    page: 'events',
    entity: 'dealer-event-park',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerStartPatrol: {
    id: 'dealer-start-patrol',
    method: 'POST',
    apiPath: '/api/dealer/events/:siteId/start-patrol',
    page: 'events',
    entity: 'dealer-start-patrol',
  } satisfies ApiEndpointDefinition<StartPatrolSessionResponse>,
  /** Exact OpenAPI path; alias siteCheckStart → /api/dealer/site-check/start */
  dealerStartSiteCheck: {
    id: 'dealer-start-site-check',
    method: 'POST',
    apiPath: '/api/dealer/events/:siteId/start-site-check',
    page: 'events',
    entity: 'dealer-start-site-check',
  } satisfies ApiEndpointDefinition<StartPatrolSessionResponse>,
  dealerRoutingAllowedSites: {
    id: 'dealer-routing-allowed-sites',
    method: 'GET',
    apiPath: '/api/dealer/routing/allowed-sites',
    page: 'events',
    entity: 'dealer-routing-allowed-sites',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerSitesArmed: {
    id: 'dealer-sites-armed',
    method: 'GET',
    apiPath: '/api/dealer/sites/armed',
    page: 'events',
    entity: 'dealer-sites-armed',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerSitesDisarmed: {
    id: 'dealer-sites-disarmed',
    method: 'GET',
    apiPath: '/api/dealer/sites/disarmed',
    page: 'events',
    entity: 'dealer-sites-disarmed',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerSitesSearch: {
    id: 'dealer-sites-search',
    method: 'GET',
    apiPath: '/api/dealer/sites/search',
    page: 'events',
    entity: 'dealer-sites-search',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerSiteById: {
    id: 'dealer-site-by-id',
    method: 'GET',
    apiPath: '/api/dealer/sites/:siteId',
    page: 'events',
    entity: 'dealer-site-by-id',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerSiteDevices: {
    id: 'dealer-site-devices',
    method: 'GET',
    apiPath: '/api/dealer/sites/:siteId/devices',
    page: 'events',
    entity: 'dealer-site-devices',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerSiteDisarm: {
    id: 'dealer-site-disarm',
    method: 'POST',
    apiPath: '/api/dealer/sites/:siteId/disarm',
    page: 'events',
    entity: 'dealer-site-disarm',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerCameraDisarm: {
    id: 'dealer-camera-disarm',
    method: 'POST',
    apiPath: '/api/dealer/cameras/:cameraId/disarm',
    page: 'events',
    entity: 'dealer-camera-disarm',
  } satisfies ApiEndpointDefinition<DisarmCameraResponse>,
  dealerCameraPtzCenter: {
    id: 'dealer-camera-ptz-center',
    method: 'POST',
    apiPath: '/api/dealer/cameras/:cameraId/ptz/center',
    page: 'events',
    entity: 'dealer-camera-ptz-center',
  } satisfies ApiEndpointDefinition<CameraPtzResponse>,
  dealerCameraPtzZoom: {
    id: 'dealer-camera-ptz-zoom',
    method: 'POST',
    apiPath: '/api/dealer/cameras/:cameraId/ptz/zoom',
    page: 'events',
    entity: 'dealer-camera-ptz-zoom',
  } satisfies ApiEndpointDefinition<CameraPtzResponse>,
  dealerTalkdownActivate: {
    id: 'dealer-talkdown-activate',
    method: 'POST',
    apiPath: '/api/dealer/talkdown/activate',
    page: 'events',
    entity: 'dealer-talkdown-activate',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerCaseCreate: {
    id: 'dealer-case-create',
    method: 'POST',
    apiPath: '/api/dealer/case',
    page: 'events',
    entity: 'dealer-case-create',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerActivitiesTypes: {
    id: 'dealer-activities-types',
    method: 'GET',
    apiPath: '/api/dealer/activities/types',
    page: 'events',
    entity: 'dealer-activities-types',
  } satisfies ApiEndpointDefinition<unknown>,
  dealerTypeValues: {
    id: 'dealer-type-values',
    method: 'GET',
    apiPath: '/api/dealer/types',
    page: 'events',
    entity: 'dealer-type-values',
  } satisfies ApiEndpointDefinition<unknown>,
  /** Dori Inference Callback — POST /flag_inference (base from environment.doriApiBaseUrl). */
  doriFlagInference: {
    id: 'dori-flag-inference',
    method: 'POST',
    apiPath: '/api/dori/flag_inference',
    page: 'events',
    entity: 'dori-flag-inference',
  } satisfies ApiEndpointDefinition<unknown>,
  doriHealth: {
    id: 'dori-health',
    method: 'GET',
    apiPath: '/api/dori/health',
    page: 'events',
    entity: 'dori-health',
  } satisfies ApiEndpointDefinition<unknown>,
  doriRoot: {
    id: 'dori-root',
    method: 'GET',
    apiPath: '/api/dori/',
    page: 'events',
    entity: 'dori-root',
  } satisfies ApiEndpointDefinition<unknown>,
} as const satisfies Record<string, ApiEndpointDefinition>;
